import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { createPortal } from "react-dom";
import { api, apiError } from "@/lib/api";
import { toast } from "sonner";
import { HandCoins, Heart, Loader2, ArrowRight, X, Target, Users } from "lucide-react";
import Footer from "@/components/Footer";
import { Seal } from "@/components/Seal";
import { useBranding } from "@/lib/branding";

const money = (n) => `$${(n || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;

export default function Give() {
  const { campaignId } = useParams();
  const [cfg, setCfg] = useState({ presets: [25, 50, 100, 250], campaigns: [] });
  const [amount, setAmount] = useState(100);
  const [custom, setCustom] = useState("");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [campaign, setCampaign] = useState(campaignId || "");
  const [busy, setBusy] = useState(false);
  const [learnMore, setLearnMore] = useState(null);
  const b = useBranding();

  useEffect(() => {
    api.get("/donations/config").then((r) => {
      setCfg(r.data);
      if (!campaignId && r.data.campaigns[0]) setCampaign(r.data.campaigns[0].id);
    }).catch(() => {});
  }, [campaignId]);

  const finalAmount = custom ? parseFloat(custom) : amount;
  const selCampaign = cfg.campaigns.find((c) => c.id === campaign);

  const donate = async () => {
    if (!finalAmount || finalAmount < 1) { toast.error("Enter a valid amount"); return; }
    if (!name.trim() || !email.trim()) { toast.error("Please add your name and email"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/donations/checkout", {
        amount: finalAmount, name, email, campaign_id: campaign, origin_url: window.location.origin,
      });
      window.location.href = data.checkout_url;
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); setBusy(false); }
  };

  const chip = (active) =>
    `px-3 py-2 rounded-full text-sm text-left transition-colors border ${active
      ? "bg-[#C8A24C] text-[#001A4B] border-[#C8A24C] font-semibold"
      : "bg-white/5 text-white/80 border-white/15 hover:bg-white/10"}`;

  return (
    <div className="min-h-screen bg-[#001A4B] text-white flex flex-col">
      <div className="max-w-5xl mx-auto w-full px-6 py-8 flex items-center gap-3">
        <Seal className="h-11 w-11" />
        <div><div className="text-xs font-mono uppercase tracking-[0.3em] text-[#C8A24C]">{b.university_name}</div>
          <div className="font-serif text-xl">Alumni Giving</div></div>
      </div>
      <div className="flex-1 flex items-center justify-center px-6 pb-16">
        <div className="grid md:grid-cols-2 gap-8 max-w-4xl w-full items-start md:pt-8">
          <div className="hidden md:block">
            <h1 className="font-serif text-4xl leading-tight">Fuel the mission.<br />Support the next generation.</h1>
            <p className="text-white/70 mt-4 text-sm leading-relaxed">Your gift sustains scholarships, faculty, and student life at {b.university_name}. Every contribution, at any level, makes a lasting difference.</p>

            {cfg.campaigns.length > 0 && (
              <div className="mt-6">
                <div className="text-[#C8A24C] text-xs font-mono uppercase tracking-widest mb-2">Choose what to support</div>
                <div className="flex flex-wrap gap-2">
                  <button data-testid="give-fund-general" onClick={() => setCampaign("")} className={chip(!campaign)}>Area of Greatest Need</button>
                  {cfg.campaigns.map((c) => (
                    <button key={c.id} data-testid={`give-fund-${c.id}`} onClick={() => setCampaign(c.id)} className={chip(campaign === c.id)}>{c.name}</button>
                  ))}
                </div>
              </div>
            )}

            {selCampaign && (
              <div className="mt-6 p-4 rounded-lg bg-white/5 border border-white/10" data-testid="give-selected-campaign">
                <div className="text-[#C8A24C] text-xs font-mono uppercase tracking-widest">Supporting</div>
                <div className="font-serif text-lg mt-1">{selCampaign.name}</div>
                {selCampaign.description && <p className="text-white/60 text-xs mt-1 line-clamp-2">{selCampaign.description}</p>}
                {selCampaign.goal ? (
                  <div className="mt-3">
                    <div className="h-1.5 rounded-full bg-white/10 overflow-hidden">
                      <div className="h-full bg-[#C8A24C]" style={{ width: `${Math.min(100, Math.round(((selCampaign.raised || 0) / selCampaign.goal) * 100))}%` }} />
                    </div>
                    <div className="flex justify-between text-[11px] text-white/50 mt-1">
                      <span>{money(selCampaign.raised)} raised</span><span>Goal {money(selCampaign.goal)}</span>
                    </div>
                  </div>
                ) : null}
                <button data-testid="give-learn-more" onClick={() => setLearnMore(selCampaign)}
                  className="mt-3 text-sm text-[#C8A24C] font-semibold inline-flex items-center gap-1 hover:gap-2 transition-all">
                  Learn more <ArrowRight className="h-4 w-4" />
                </button>
              </div>
            )}
          </div>

          <div className="bg-white text-foreground rounded-xl shadow-2xl p-6">
            <div className="flex items-center gap-2 mb-4"><HandCoins className="h-5 w-5 text-primary" /><span className="font-serif text-xl">Make a Gift</span></div>
            <div className="grid grid-cols-4 gap-2">
              {cfg.presets.map((p) => (
                <button key={p} data-testid={`give-preset-${p}`} onClick={() => { setAmount(p); setCustom(""); }}
                  className={`py-2.5 rounded-md border text-sm font-semibold transition-colors ${!custom && amount === p ? "border-primary bg-primary/10 text-primary" : "border-border hover:bg-muted"}`}>${p}</button>
              ))}
            </div>
            <div className="mt-3 relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">$</span>
              <input data-testid="give-custom" type="number" min="1" value={custom} onChange={(e) => setCustom(e.target.value)} placeholder="Other amount" className="ud-input pl-7" />
            </div>
            {cfg.campaigns.length > 0 && (
              <label className="block mt-3"><span className="text-xs font-mono uppercase tracking-wider text-muted-foreground">Designate to</span>
                <select data-testid="give-campaign" value={campaign} onChange={(e) => setCampaign(e.target.value)} className="ud-input mt-1">
                  <option value="">Area of Greatest Need</option>
                  {cfg.campaigns.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
              </label>
            )}
            <input data-testid="give-name" value={name} onChange={(e) => setName(e.target.value)} placeholder="Full name" className="ud-input mt-3" />
            <input data-testid="give-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Email" className="ud-input mt-3" />
            <button data-testid="give-submit" onClick={donate} disabled={busy} className="mt-4 w-full flex items-center justify-center gap-2 py-3 rounded-md bg-[#C8A24C] text-[#001A4B] font-bold hover:brightness-105 disabled:opacity-60">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Heart className="h-4 w-4" />} Give ${finalAmount || 0}
            </button>
            <p className="text-[11px] text-muted-foreground text-center mt-3">Secure payment via Stripe · test mode</p>
          </div>
        </div>
      </div>
      <Footer dark />

      {learnMore && <LearnMoreModal campaign={learnMore} onClose={() => setLearnMore(null)} onGive={() => { setCampaign(learnMore.id); setLearnMore(null); }} />}
    </div>
  );
}

const LearnMoreModal = ({ campaign, onClose, onGive }) => {
  const pct = campaign.goal ? Math.min(100, Math.round(((campaign.raised || 0) / campaign.goal) * 100)) : null;
  const story = campaign.long_description || campaign.description || "More details about this fund are coming soon. Your gift makes an immediate difference for Crusaders.";
  return createPortal((
    <div className="fixed inset-0 z-50 flex items-start justify-center p-4 bg-black/70 overflow-y-auto" onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="bg-white text-foreground w-full max-w-xl rounded-xl shadow-2xl my-8" onMouseDown={(e) => e.stopPropagation()}>
        <div className="bg-[#001A4B] text-white rounded-t-xl px-6 py-5 flex items-start justify-between">
          <div className="flex items-center gap-3">
            <Seal className="h-11 w-11" />
            <div>
              <div className="text-[#C8A24C] text-xs font-mono uppercase tracking-widest">Alumni Giving · Campaign</div>
              <h2 className="font-serif text-2xl mt-1">{campaign.name}</h2>
            </div>
          </div>
          <button data-testid="learn-more-close" onClick={onClose} className="text-white/70 hover:text-white"><X className="h-5 w-5" /></button>
        </div>
        <div className="p-6">
          {pct !== null && (
            <div className="mb-5">
              <div className="h-2 rounded-full bg-muted overflow-hidden"><div className="h-full bg-[#C8A24C]" style={{ width: `${pct}%` }} /></div>
              <div className="flex flex-wrap gap-4 mt-2 text-sm text-muted-foreground">
                <span className="font-semibold text-foreground">{money(campaign.raised)} raised</span>
                <span className="flex items-center gap-1"><Target className="h-3.5 w-3.5" /> Goal {money(campaign.goal)}</span>
                {campaign.donor_count ? <span className="flex items-center gap-1"><Users className="h-3.5 w-3.5" /> {campaign.donor_count} donor{campaign.donor_count === 1 ? "" : "s"}</span> : null}
              </div>
            </div>
          )}
          <div className="text-sm leading-relaxed whitespace-pre-wrap text-foreground/90" data-testid="learn-more-story">{story}</div>
          <button data-testid="learn-more-give" onClick={onGive} className="mt-6 w-full flex items-center justify-center gap-2 py-3 rounded-md bg-[#C8A24C] text-[#001A4B] font-bold hover:brightness-105">
            <Heart className="h-4 w-4" /> Give to {campaign.name}
          </button>
        </div>
      </div>
    </div>
  ), document.body);
};
